import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'

import TimedOutPage from 'components/ErrorBoundry/pages/TimedOut'

interface ITimedOutContext {
  reportTimedOut: () => void
}

const TimedOutContext = createContext<ITimedOutContext | undefined>(undefined)

export const useTimedOut = () => {
  const context = useContext(TimedOutContext)
  if (context === undefined) {
    throw new Error('useTimedOut must be used within a TimedOutProvider')
  }
  return context
}

interface ITimedOutProps {
  children: React.ReactNode
}

/**
 * Renders the Timed Out page in place of the activity once the WAF rejects a
 * request on a bot-protected distribution.
 *
 * Rendered in place rather than pushed as a route: the page tells the
 * respondent to reload, and a reload only re-runs the CloudFront challenge
 * while the URL is still the protected one they arrived on.
 */
export const TimedOutProvider = (props: ITimedOutProps) => {
  const { children } = props
  const [hasTimedOut, setHasTimedOut] = useState(false)

  // Reporting is idempotent: repeat reports set the same value, so the page is never re-mounted.
  const reportTimedOut = useCallback(() => setHasTimedOut(true), [])

  const value = useMemo(() => ({ reportTimedOut }), [reportTimedOut])

  return (
    <TimedOutContext.Provider value={value}>
      {hasTimedOut ? <TimedOutPage /> : children}
    </TimedOutContext.Provider>
  )
}
